import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Badge, Button } from "web-design-system";
import { PortfolioReview } from "creso-core/types";
import { Page } from "@core/components/layout/page";
import { format } from "date-fns";
import { EditColumnsMenu } from "./edit-columns-menu";
import {
  DEFAULT_VISIBLE_COLUMNS,
  HOLDING_COLUMNS,
  HoldingColumnKey,
} from "./holding-columns";
import { HoldingsTable } from "./holdings-table";
import { LiveInsights } from "./live-insights";
import { PickFundsDialog } from "./pick-funds-dialog";
import { PortfolioChangedBanner } from "./portfolio-changed-banner";
import { ReviewReportDialog } from "./review-report-dialog";

export function ReviewWorkbench({ review }: { review: PortfolioReview }) {
  const [visibleColumns, setVisibleColumns] = useState<HoldingColumnKey[]>(
    DEFAULT_VISIBLE_COLUMNS
  );
  const [pickingFunds, setPickingFunds] = useState(false);
  const [exporting, setExporting] = useState(false);
  const firstName = review.client_name?.split(" ")[0];

  return (
    <Page>
      <Page.Content className="lg:overflow-hidden">
        <Page.Header
          title={
            <div className="flex items-center gap-4">
              <Button variant="outline" size="icon" aria-label="Back" asChild>
                <Link
                  to="/clients/$id/review"
                  params={{ id: String(review.client_id) }}
                >
                  <ArrowLeft />
                </Link>
              </Button>
              <h4 className="whitespace-nowrap">
                {firstName
                  ? `${firstName}’s Portfolio Review`
                  : "Portfolio Review"}
              </h4>
              {review.status === "FINALIZED" ? (
                <Badge
                  variant="custom"
                  className="bg-tag-green font-semibold text-tag-green-foreground"
                >
                  Finalized
                </Badge>
              ) : null}
              {review.as_of_date && (
                <Badge
                  variant="custom"
                  className="bg-tag-slate px-2 py-1 font-normal text-tag-slate-foreground"
                >
                  Holdings as of {format(review.as_of_date, "PPP")}
                </Badge>
              )}
            </div>
          }
          actions={
            <Button onClick={() => setExporting(true)}>Export Report</Button>
          }
        />
        {/* On desktop the holdings and insights scroll independently, each
            filling the height below the header. */}
        <Page.Body className="grid grid-cols-1 items-start gap-6 lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_25rem] lg:grid-rows-[minmax(0,1fr)] lg:items-stretch">
          <section className="flex min-w-0 flex-col gap-6 lg:overflow-y-auto">
            <PortfolioChangedBanner review={review} />
            <div className="flex items-center justify-between gap-4">
              <h5>Holdings</h5>
              <EditColumnsMenu
                columns={HOLDING_COLUMNS}
                visible={visibleColumns}
                onChange={setVisibleColumns}
              />
            </div>
            <HoldingsTable
              review={review}
              visibleColumns={visibleColumns}
              onRecommendFunds={() => setPickingFunds(true)}
            />
          </section>
          <LiveInsights review={review} className="lg:overflow-y-auto" />
        </Page.Body>
        <PickFundsDialog
          review={review}
          open={pickingFunds}
          onOpenChange={setPickingFunds}
        />
        <ReviewReportDialog
          review={review}
          open={exporting}
          onOpenChange={setExporting}
        />
      </Page.Content>
    </Page>
  );
}
